import { DEFAULT_OPTIONS, type Options } from '../config/config';
import { DEFAULT_BINDINGS, type Bindings } from './input';

const KEY = 'sunrise-vale.save.v1';

export interface SaveData {
  hiscore: number;
  options: Options;
  bindings: Bindings;
}

function blank(): SaveData {
  return {
    hiscore: 0,
    options: { ...DEFAULT_OPTIONS },
    bindings: cloneBindings(DEFAULT_BINDINGS),
  };
}

export function cloneBindings(b: Bindings): Bindings {
  const out = {} as Bindings;
  for (const k of Object.keys(b) as (keyof Bindings)[]) out[k] = [...b[k]];
  return out;
}

function safeLocalStorage(): Storage | null {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const probe = '__sv_probe__';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return ls;
  } catch {
    return null; // private mode / disabled storage — run without persistence
  }
}

/** Persisted save slot: high score, audio + gameplay options and key bindings. */
export class Save {
  data: SaveData = blank();
  private ls = safeLocalStorage();

  constructor() {
    this.load();
  }

  load(): void {
    this.data = blank();
    const raw = this.ls?.getItem(KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Partial<SaveData>;
      if (typeof parsed.hiscore === 'number' && isFinite(parsed.hiscore)) {
        this.data.hiscore = Math.max(0, Math.floor(parsed.hiscore));
      }
      if (parsed.options) {
        for (const k of Object.keys(DEFAULT_OPTIONS) as (keyof Options)[]) {
          const v = (parsed.options as Record<string, unknown>)[k];
          if (v !== undefined && typeof v === typeof DEFAULT_OPTIONS[k]) {
            (this.data.options as Record<string, unknown>)[k] = v;
          }
        }
      }
      if (parsed.bindings) {
        for (const k of Object.keys(DEFAULT_BINDINGS) as (keyof Bindings)[]) {
          const v = (parsed.bindings as Record<string, unknown>)[k];
          if (Array.isArray(v) && v.every((s) => typeof s === 'string') && v.length) {
            this.data.bindings[k] = v as string[];
          }
        }
      }
    } catch {
      /* corrupted save — fall back to defaults */
    }
  }

  save(): void {
    try {
      this.ls?.setItem(KEY, JSON.stringify(this.data));
    } catch {
      /* quota / disabled — ignore */
    }
  }

  clear(): void {
    try {
      this.ls?.removeItem(KEY);
    } catch {
      /* ignore */
    }
    this.data = blank();
  }

  submitScore(score: number): boolean {
    if (score > this.data.hiscore) {
      this.data.hiscore = score;
      this.save();
      return true;
    }
    return false;
  }
}
